import { useEffect, useMemo, useState } from "react";
import { Input, Modal } from "@iq/models";
import { getIncidents, linkProblemToIncident } from "../../services/ticket_service";
import "./LinkIncidentModal.css";

function formatTicketLabel(incident) {
    const number = incident?.number || `INC-${incident?.id ?? "?"}`;
    const title = incident?.title || "Untitled Incident";
    return `${number} - ${title}`;
}

export default function LinkIncidentModal({
    isOpen,
    onClose,
    problem,
    onCompleted,
}) {
    const [selectedIncidentId, setSelectedIncidentId] = useState("");
    const [loadingIncidents, setLoadingIncidents] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [incidentSearch, setIncidentSearch] = useState("");
    const [incidents, setIncidents] = useState([]);

    const filteredIncidents = useMemo(() => {
        const search = incidentSearch.trim().toLowerCase();

        return (Array.isArray(incidents) ? incidents : [])
            .filter((incident) => {
                if (!search) {
                    return true;
                }

                return [incident?.number, incident?.title, incident?.description]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(search));
            })
            .sort((left, right) => new Date(right?.created_at || 0) - new Date(left?.created_at || 0));
    }, [incidentSearch, incidents]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setSelectedIncidentId("");
        setIncidentSearch("");
        setError("");
        setSaving(false);
        setLoadingIncidents(true);

        let cancelled = false;

        (async () => {
            try {
                const loadedIncidents = await getIncidents({ limit: 200 });
                if (!cancelled) {
                    setIncidents(Array.isArray(loadedIncidents) ? loadedIncidents : []);
                }
            } catch (incidentError) {
                if (!cancelled) {
                    setIncidents([]);
                    setError(incidentError?.message || "Failed to load incidents");
                }
            } finally {
                if (!cancelled) {
                    setLoadingIncidents(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    const selectedIncident = useMemo(
        () => filteredIncidents.find((incident) => String(incident?.id) === String(selectedIncidentId)),
        [filteredIncidents, selectedIncidentId]
    );

    async function handleSubmit(event) {
        event.preventDefault();

        if (!problem?.id || !selectedIncidentId) {
            return;
        }

        setSaving(true);
        setError("");

        try {
            await linkProblemToIncident(problem.id, Number(selectedIncidentId));
            onCompleted?.({ problemId: problem.id, incidentId: Number(selectedIncidentId), incident: selectedIncident || null });
            onClose?.();
        } catch (submitError) {
            setError(submitError?.message || "Failed to link incident");
        } finally {
            setSaving(false);
        }
    }

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Link Incident"
            actionButtons={[
                {
                    key: "cancel-link-incident",
                    text: "Cancel",
                    color: "light",
                    onClick: onClose,
                    disabled: saving,
                },
                {
                    key: "submit-link-incident",
                    text: saving ? "Linking..." : "Link Incident",
                    color: "accent",
                    type: "submit",
                    form: "link-incident-form",
                    disabled: saving || !selectedIncidentId,
                },
            ]}
            errorMessage={error}
            tall
        >
            <form id="link-incident-form" onSubmit={handleSubmit} className="link-incident-modal-form">
                <div>
                    <label htmlFor="incident-search">Search Incidents</label>
                    <Input
                        id="incident-search"
                        type="search"
                        name="incident-search"
                        value={incidentSearch}
                        onChange={(event) => setIncidentSearch(event.target.value)}
                        placeholder="Search by incident number, title, or description"
                        disabled={loadingIncidents}
                        colorScheme="light"
                    />
                </div>

                <div className="link-incident-panel-title-row">
                    <h3>Incidents</h3>
                    <span>{filteredIncidents.length} found</span>
                </div>

                <div className="link-incident-list" role="listbox" aria-label="Incidents">
                    {loadingIncidents ? (
                        <div className="modal-empty-state">Loading incidents...</div>
                    ) : filteredIncidents.length === 0 ? (
                        <div className="modal-empty-state">No incidents found.</div>
                    ) : (
                        filteredIncidents.map((incident) => {
                            const isSelected = String(selectedIncidentId) === String(incident?.id);

                            return (
                                <button
                                    key={incident.id}
                                    type="button"
                                    className={[
                                        "link-incident-list-item",
                                        isSelected ? "link-incident-list-item--selected" : "",
                                    ].filter(Boolean).join(" ")}
                                    onClick={() => {
                                        setSelectedIncidentId(String(incident.id));
                                        setError("");
                                    }}
                                >
                                    <div className="link-incident-list-item__headline">{incident?.title}</div>
                                    <div className="link-incident-list-item__meta">{incident.number}</div>
                                    <div className="link-incident-list-item__meta">
                                        <span>Status: {incident?.status || "-"}</span>
                                        <span>Priority: {incident?.priority || "-"}</span>
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>

                {selectedIncident ? (
                    <div className="link-incident-selected-summary">
                        Selected: {formatTicketLabel(selectedIncident)}
                    </div>
                ) : null}
            </form>
        </Modal>
    );
}
